import React, { useCallback, useEffect, useRef, useState } from "react"
import axios from "axios"
import { NavLink, Outlet } from "react-router-dom"
import { SidebarProvider, SidebarInset, SidebarTrigger, SidebarSeparator, useSidebar } from "@/components/ui/sidebar"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuGroup,
} from "@/components/ui/dropdown-menu"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { getImageUrl } from "@/utils/imagePaths"
import { Separator } from "@/components/ui/separator"
import { useAuth } from "@/contexts/AuthContext"
import { useTheme } from "@/contexts/ThemeContext"
import { ArrowRightOnRectangleIcon, SunIcon, MoonIcon, ComputerDesktopIcon, Cog6ToothIcon, ChatBubbleLeftRightIcon, BellIcon } from "@heroicons/react/24/outline"
import { useNavigate } from "react-router-dom"
import Sidebar from "./AppSidebar"
import SearchDialog from "../SearchDialog"
import SearchPreviewModal, { PreviewEntity } from "../SearchPreviewModal"
import AlertsButton from "../AlertsButton"
import { getAppSocket } from "@/lib/wesSocket"
import { WHATSAPP_CHAT_ENABLED } from "@/config/modules"
import { alTocarAvisoPush, sincronizarPush } from "@/utils/push"

const LayoutContent: React.FC = () => {
  const navigate = useNavigate()
  const { user, logout, hasPermission } = useAuth()
  const { isDarkMode, themeMode, setThemeMode } = useTheme()
  const { toggleSidebar } = useSidebar()
  const [searchOpen, setSearchOpen] = useState(false)
  const [previewEntity, setPreviewEntity] = useState<PreviewEntity | null>(null)
  const [chatUnreadCount, setChatUnreadCount] = useState(0)
  // Módulo de chat apagado: sin badge ni polling de no-leídos
  const canSeeChat = WHATSAPP_CHAT_ENABLED && (hasPermission('clients') || hasPermission('whatsapp'))
  const soundAudioRef = useRef<HTMLAudioElement | null>(null)
  const soundAudioContextRef = useRef<AudioContext | null>(null)
  const soundUnlockedRef = useRef(false)
  const lastSoundAtRef = useRef(0)

  // Notificaciones push: al entrar, este dispositivo queda a nombre de quien inició sesión;
  // y si tocan un aviso con el sistema ya abierto, se va a esa pantalla sin recargar.
  useEffect(() => {
    if (!user?.id) return undefined
    sincronizarPush()
    return alTocarAvisoPush((url) => navigate(url))
  }, [user?.id, navigate])

  const getUserInitials = () => {
    const firstName = user?.firstName || ""
    const lastName = user?.lastName || ""
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
  }

  const getUserName = () => {
    return `${user?.firstName || ""} ${user?.lastName || ""}`.trim()
      || user?.email
      || "Usuario"
  }

  const avatarSrc = (() => {
    if (user?.avatar) {
      return getImageUrl(user.avatar)
    }
    if (user?.id) {
      const stored = localStorage.getItem(`userAvatar-${user.id}`)
      if (stored) {
        return getImageUrl(stored)
      }
    }
    return ''
  })()

  // Polling de no-leídos de chat para badge en header
  useEffect(() => {
    if (!canSeeChat) { setChatUnreadCount(0); return }
    let cancelled = false
    let inFlight = false
    const fetchUnread = async () => {
      if (inFlight) return
      inFlight = true
      try {
        const res = await axios.get("/api/whatsapp/chat/unread-count")
        if (!cancelled) setChatUnreadCount(Number(res?.data?.unread || 0))
      } catch { if (!cancelled) setChatUnreadCount(0) }
      finally { inFlight = false }
    }
    fetchUnread()
    const timer = window.setInterval(fetchUnread, 3000)
    return () => { cancelled = true; window.clearInterval(timer) }
  }, [canSeeChat])

  // Atajo de teclado Ctrl+B para toggle sidebar
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'b') {
        event.preventDefault()
        toggleSidebar()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [toggleSidebar])

  useEffect(() => {
    const playBeepFallback = async () => {
      try {
        let ctx = soundAudioContextRef.current
        if (!ctx) {
          const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext
          ctx = Ctx ? new Ctx() : null
          soundAudioContextRef.current = ctx
        }
        if (!ctx) return
        if (ctx.state === 'suspended') await ctx.resume()
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = 'sine'
        osc.frequency.value = 920
        gain.gain.value = 0.065
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.start()
        osc.stop(ctx.currentTime + 0.14)
      } catch {
        // Ignorar errores de audio.
      }
    }

    const playNotificationSound = () => {
      const now = Date.now()
      if (now - lastSoundAtRef.current < 1500) return
      lastSoundAtRef.current = now
      try {
        if (!soundAudioRef.current) {
          soundAudioRef.current = new Audio('/sounds/f1-radio-notification.mp3')
          soundAudioRef.current.preload = 'auto'
          soundAudioRef.current.volume = 1
        }
        const audio = soundAudioRef.current
        audio.currentTime = 0
        let started = false
        const onPlaying = () => {
          started = true
          audio.removeEventListener('playing', onPlaying)
        }
        audio.addEventListener('playing', onPlaying)
        audio.play().catch(() => playBeepFallback())
        window.setTimeout(() => {
          if (!started) playBeepFallback()
        }, 450)
      } catch {
        playBeepFallback()
      }
    }

    const unlockAudio = () => {
      if (soundUnlockedRef.current) return
      soundUnlockedRef.current = true
      try {
        if (!soundAudioContextRef.current) {
          const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext
          if (Ctx) soundAudioContextRef.current = new Ctx()
        }
        if (soundAudioContextRef.current?.state === 'suspended') {
          soundAudioContextRef.current.resume().catch(() => {})
        }
        if (!soundAudioRef.current) {
          soundAudioRef.current = new Audio('/sounds/f1-radio-notification.mp3')
          soundAudioRef.current.preload = 'auto'
        }
        const audio = soundAudioRef.current
        audio.volume = 0.0001
        audio.load()
        audio.play()
          .then(() => {
            audio.pause()
            audio.currentTime = 0
            audio.volume = 1
          })
          .catch(() => {
            audio.volume = 1
          })
      } catch {
        // Ignorar errores de unlock.
      }
    }

    const socket = getAppSocket()
    const onNewNotification = (payload?: any) => {
      const type = String(payload?.type || payload?.notification?.type || '').toLowerCase()
      if (type === 'task' || type === 'wes_action') {
        playNotificationSound()
      }
    }
    const onWesUpdate = (payload?: any) => {
      const type = String(payload?.type || '').toLowerCase()
      if (type === 'created') {
        playNotificationSound()
      }
    }
    socket.on('new-notification', onNewNotification)
    socket.on('wes:update', onWesUpdate)
    window.addEventListener('pointerdown', unlockAudio, { passive: true })
    window.addEventListener('keydown', unlockAudio)
    window.addEventListener('touchstart', unlockAudio, { passive: true })

    return () => {
      socket.off('new-notification', onNewNotification)
      socket.off('wes:update', onWesUpdate)
      window.removeEventListener('pointerdown', unlockAudio)
      window.removeEventListener('keydown', unlockAudio)
      window.removeEventListener('touchstart', unlockAudio)
    }
  }, [])

  return (
    <>
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} onPreview={setPreviewEntity} />
      <SearchPreviewModal entity={previewEntity} onClose={() => setPreviewEntity(null)} />
      <Sidebar />
      <SidebarInset>
        <header className="flex h-14 shrink-0 items-center gap-2 transition-[width,height] ease-linear">
          <div className="flex w-full items-center gap-1 px-3 lg:gap-2 lg:px-4 xl:px-6">
            <TooltipPrimitive.Provider delayDuration={0}>
              <TooltipPrimitive.Root>
                <TooltipPrimitive.Trigger asChild>
                  <SidebarTrigger className="-ml-1" />
                </TooltipPrimitive.Trigger>
                <TooltipPrimitive.Portal>
                  <TooltipPrimitive.Content
                    side="right"
                    sideOffset={8}
                    className="bg-gray-900 dark:bg-white text-white dark:text-gray-900 animate-in fade-in-0 zoom-in-95 z-50 rounded-md px-3 py-1.5 text-xs"
                  >
                    <div className="flex items-center gap-2">
                      <span>Abrir/Cerrar menú</span>
                      <div className="hidden md:flex items-center justify-center gap-1">
                        <kbd className="px-1 font-bold font-sans bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 rounded-sm border">
                          Ctrl
                        </kbd>
                        <kbd className="px-1 text-xs font-bold font-sans bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 rounded-sm border">
                          B
                        </kbd>
                      </div>
                    </div>
                    <TooltipPrimitive.Arrow className="bg-gray-900 dark:bg-white fill-gray-900 dark:fill-white" />
                  </TooltipPrimitive.Content>
                </TooltipPrimitive.Portal>
              </TooltipPrimitive.Root>
            </TooltipPrimitive.Provider>
            <Separator orientation="vertical" className="mx-2 h-4" />

            <div className="ml-auto flex items-center gap-1 md:gap-2">
              {/* Buscador mobile - solo icono */}
              <button
                onClick={() => setSearchOpen(true)}
                className="md:hidden flex items-center justify-center h-8 w-8 rounded-full border bg-card shadow-xs hover:bg-accent text-muted-foreground"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                </svg>
              </button>
              {/* Buscador desktop */}
              <button
                onClick={() => setSearchOpen(true)}
                className="cursor-pointer items-center whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border active:scale-[0.97] bg-card shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 h-8 rounded-lg px-3 has-[>svg]:px-2.5 hidden md:flex gap-2 w-48 justify-between text-muted-foreground"
              >
                Buscar...
                <div className="hidden md:flex items-center justify-center gap-1 text-xs text-muted-foreground">
                  <kbd className="px-1 font-bold font-sans text-muted-foreground rounded-sm border">Ctrl</kbd>
                  <kbd className="px-1 text-xs font-bold font-sans text-muted-foreground rounded-sm border">K</kbd>
                </div>
              </button>

              {/* Botón de Chat (oculto con el módulo apagado) */}
              {WHATSAPP_CHAT_ENABLED && (hasPermission('whatsapp') || hasPermission('reports')) && (
                <NavLink
                  to="/chat"
                  className={({ isActive }) =>
                    `relative cursor-pointer items-center justify-center gap-2 whitespace-nowrap text-sm font-medium disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] border active:scale-[0.97] bg-card shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 px-4 py-2 has-[>svg]:px-3 flex size-8 rounded-full transition-transform duration-200 ${isActive ? 'text-green-500 border-green-500/40' : 'text-green-500'}`
                  }
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0012.04 2m.01 1.67c2.2 0 4.26.86 5.82 2.42a8.23 8.23 0 012.41 5.83c0 4.54-3.7 8.23-8.24 8.23-1.48 0-2.93-.39-4.19-1.15l-.3-.17-3.12.82.83-3.04-.2-.32a8.15 8.15 0 01-1.26-4.38c.01-4.54 3.7-8.24 8.25-8.24M8.53 7.33c-.16 0-.43.06-.66.31-.22.25-.87.86-.87 2.07 0 1.22.89 2.39 1 2.56.14.17 1.76 2.67 4.25 3.73.59.27 1.05.42 1.41.53.59.19 1.13.16 1.56.1.48-.07 1.46-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.07-.1-.23-.16-.48-.29-.25-.12-1.47-.72-1.69-.81-.23-.08-.37-.12-.56.12-.16.25-.65.81-.79.97-.15.17-.29.19-.53.07-.26-.13-1.06-.39-2-1.23-.74-.66-1.23-1.47-1.38-1.72-.12-.24-.01-.37.11-.49.11-.11.25-.29.36-.44.13-.14.17-.25.25-.41.08-.17.04-.31-.02-.44-.07-.11-.57-1.35-.78-1.84-.2-.48-.4-.42-.56-.43-.14 0-.3-.01-.47-.01z" />
                  </svg>
                  {chatUnreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                      {chatUnreadCount > 99 ? '99+' : chatUnreadCount}
                    </span>
                  )}
                </NavLink>
              )}

              {/* Botón de Alertas */}
              {hasPermission('alerts') && <AlertsButton />}

              <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-lg p-1.5 transition-colors hover:bg-muted shrink-0">
                <Avatar className="h-8 w-8 border-2 border-border">
                  {avatarSrc && <AvatarImage src={avatarSrc} alt={getUserName()} />}
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
                <div
                  className="flex max-w-[140px] flex-col items-start text-left cursor-pointer leading-tight"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/profile');
                  }}
                >
                  <span className="text-sm font-medium hover:underline truncate">{getUserName()}</span>
                  <span className="text-xs text-muted-foreground capitalize truncate">{user?.role || "Usuario"}</span>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end">
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <Avatar className="h-8 w-8 rounded-lg">
                    {avatarSrc && <AvatarImage src={avatarSrc} alt={getUserName()} />}
                    <AvatarFallback className="rounded-lg">
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{getUserName()}</span>
                    <span className="truncate text-xs">{user?.email}</span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="cursor-pointer">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 h-4 w-4 text-muted-foreground">
                      <circle cx="13.5" cy="6.5" r=".5" fill="currentColor"></circle>
                      <circle cx="17.5" cy="10.5" r=".5" fill="currentColor"></circle>
                      <circle cx="8.5" cy="7.5" r=".5" fill="currentColor"></circle>
                      <circle cx="6.5" cy="12.5" r=".5" fill="currentColor"></circle>
                      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"></path>
                    </svg>
                    Tema
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem 
                      className="cursor-pointer" 
                      onClick={() => setThemeMode('light')}
                    >
                      <SunIcon className="mr-2 h-4 w-4" />
                      Claro
                      {themeMode === 'light' && <span className="ml-auto">✓</span>}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="cursor-pointer" 
                      onClick={() => setThemeMode('dark')}
                    >
                      <MoonIcon className="mr-2 h-4 w-4" />
                      Oscuro
                      {themeMode === 'dark' && <span className="ml-auto">✓</span>}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="cursor-pointer" 
                      onClick={() => setThemeMode('system')}
                    >
                      <ComputerDesktopIcon className="mr-2 h-4 w-4" />
                      Sistema
                      {themeMode === 'system' && <span className="ml-auto">✓</span>}
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              </DropdownMenuGroup>
              <DropdownMenuItem onClick={() => navigate('/profile#notificaciones')} className="cursor-pointer">
                <BellIcon className="mr-2 h-4 w-4" />
                Notificaciones
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                <Cog6ToothIcon className="mr-2 h-4 w-4" />
                Mi perfil
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-600 focus:text-red-600">
                <ArrowRightOnRectangleIcon className="mr-2 h-4 w-4" />
                Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
            </div>
          </div>
        </header>
        <main className="relative flex-1 overflow-y-auto">
          <div className="py-4 lg:py-6">
            <div className="mx-auto w-full max-w-[1600px] px-3 sm:px-4 lg:px-6 xl:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </SidebarInset>
    </>
  )
}

const Layout: React.FC = () => {
  const { user } = useAuth()

  if (!user) {
    return null
  }

  // Sidebar inicia colapsado en pantallas chicas (notebook estandar < 1280px).
  // El usuario puede abrirlo/cerrarlo manualmente con el toggle.
  const initialOpen = typeof window !== 'undefined' ? window.innerWidth >= 1280 : true

  return (
    <SidebarProvider defaultOpen={initialOpen}>
      <LayoutContent />
    </SidebarProvider>
  )
}

export default Layout



